import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosConfig';

const AdminQuestStats = ({ selectedSchool, isSuperAdmin }) => {
  const [quests, setQuests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const fetchQuests = async () => {
      if (!selectedSchool) return;

      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(`/api/v1/quest/school/${selectedSchool}`);
        setQuests(response.data.quests || []);
      } catch (err) {
        setError(err.response?.data?.detail || err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchQuests();
  }, [selectedSchool]);

  if (!selectedSchool) return null;

  // Calculate statistics
  const stats = {
    totalQuests: quests.length,
    averageHoursPerWeek:
      quests.reduce((acc, curr) => acc + curr.quest.hours_per_week, 0) / (quests.length || 1),
    gradYearDistribution: quests.reduce((acc, curr) => {
      acc[curr.quest.grad_year] = (acc[curr.quest.grad_year] || 0) + 1;
      return acc;
    }, {}),
    completedTasks: quests.reduce((acc, curr) => {
      return (
        acc +
        curr.quest.quest_plan.reduce((taskAcc, week) => {
          const tasks = week.tasks || {};
          return (
            taskAcc +
            (tasks.leetcode?.filter((task) => task.completed).length || 0) +
            (tasks.apps?.filter((task) => task.completed).length || 0) +
            (tasks.lessons?.filter((task) => task.completed).length || 0) +
            (tasks.resume?.filter((task) => task.completed).length || 0)
          );
        }, 0)
      );
    }, 0),
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Quest Analytics</h2>
        {isSuperAdmin && (
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 flex items-center"
          >
            {showDetails ? 'Hide Details' : 'Show Details'}
            <svg
              className={`w-5 h-5 ml-2 transform transition-transform duration-200 ${showDetails ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : error ? (
        <div className="text-red-600 text-center p-4">{error}</div>
      ) : (
        <>
          {/* Stats Grid Section */}
          <div
            className={`${showDetails ? 'bg-gray-50' : ''} rounded-lg ${showDetails ? 'p-6' : 'p-2'} mb-6`}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Total Quests Card */}
              <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">Total Quests</p>
                    <p className="text-xl font-semibold text-gray-700">{stats.totalQuests}</p>
                  </div>
                </div>
              </div>

              {/* Average Hours Card */}
              <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-green-100 text-green-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">Avg Hours/Week</p>
                    <p className="text-xl font-semibold text-gray-700">
                      {stats.averageHoursPerWeek.toFixed(1)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Total Tasks Card */}
              <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-purple-100 text-purple-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">Completed Tasks</p>
                    <p className="text-xl font-semibold text-gray-700">{stats.completedTasks}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Sections */}
          {showDetails && (
            <>
              {/* Quest Details Table - Only show for super admins */}
              {isSuperAdmin && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Quest Details</h3>
                  <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            User
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Hours/Week
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Grad Year
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Start Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tasks Completed
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {quests.length > 0 ? (
                          quests.map((questData) => {
                            const { quest, user } = questData;
                            const completedTasks = quest.quest_plan.reduce((acc, week) => {
                              const tasks = week.tasks || {};
                              return (
                                acc +
                                (tasks.leetcode?.filter((task) => task.completed).length || 0) +
                                (tasks.apps?.filter((task) => task.completed).length || 0) +
                                (tasks.lessons?.filter((task) => task.completed).length || 0) +
                                (tasks.resume?.filter((task) => task.completed).length || 0)
                              );
                            }, 0);

                            return (
                              <tr key={quest.quest_id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {user.first_name} {user.last_name}
                                  <br />
                                  <span className="text-gray-500">{user.email}</span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {quest.hours_per_week}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {quest.grad_year}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {new Date(quest.start_date).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900">
                                  <div className="max-h-32 overflow-y-auto">
                                    {quest.quest_plan.map((week, weekIndex) => {
                                      const tasks = week.tasks || {};
                                      const completedTasks = [
                                        ...(tasks.leetcode
                                          ?.filter((task) => task.completed)
                                          .map((task) => `LeetCode: ${task.problem_name}`) || []),
                                        ...(tasks.apps
                                          ?.filter((task) => task.completed)
                                          .map((task) => `Application: ${task.company_name}`) ||
                                          []),
                                        ...(tasks.lessons
                                          ?.filter((task) => task.completed)
                                          .map((task) => `Lesson: ${task.lesson_name}`) || []),
                                        ...(tasks.resume
                                          ?.filter((task) => task.completed)
                                          .map((task) => `Resume: ${task.name}`) || []),
                                      ];

                                      return completedTasks.length > 0 ? (
                                        <div key={weekIndex} className="mb-2">
                                          <div className="font-medium text-gray-700 mb-1">
                                            Week {weekIndex + 1}
                                          </div>
                                          <ul className="list-disc list-inside space-y-1">
                                            {completedTasks.map((task, taskIndex) => (
                                              <li key={taskIndex} className="text-gray-600">
                                                {task}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      ) : null;
                                    })}
                                    {quest.quest_plan.every((week) => {
                                      const tasks = week.tasks || {};
                                      return (
                                        !tasks.leetcode?.some((t) => t.completed) &&
                                        !tasks.apps?.some((t) => t.completed) &&
                                        !tasks.lessons?.some((t) => t.completed) &&
                                        !tasks.resume?.some((t) => t.completed)
                                      );
                                    }) && (
                                      <div className="text-gray-500 italic">No completed tasks</div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                              No quests found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AdminQuestStats;
